import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  // Query, // Query type might not be needed directly
  DocumentData,
  QueryDocumentSnapshot,
  Timestamp,
} from 'firebase/firestore';
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
} from 'date-fns';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Session } from '../types/firestoreTypes';

const SESSIONS_PER_PAGE = 10; // Constant for pagination limit

// Helper function to calculate date ranges based on filter value
const getDateRange = (
  filterValue: string
): { start: Date; end: Date } | null => {
  const now = new Date();
  switch (filterValue) {
    case 'thisMonth':
      return { start: startOfMonth(now), end: endOfMonth(now) };
    case 'lastMonth':
      const lastMonth = subMonths(now, 1);
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
    case 'thisQuarter':
      return { start: startOfQuarter(now), end: endOfQuarter(now) };
    case 'lastQuarter':
      const lastQuarter = subQuarters(now, 1);
      return {
        start: startOfQuarter(lastQuarter),
        end: endOfQuarter(lastQuarter),
      };
    case 'all': // 'all' means no date range filter
    default:
      return null; // Return null if no specific range is needed
  }
};

// Define the return type for the hook
interface UsePatientSessionsReturn {
  sessions: Session[];
  isLoading: boolean;
  isFetchingMore: boolean;
  error: string | null; // Error will hold the translated string
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => void;
}

export const usePatientSessions = (
  patientId: string,
  selectedDateFilter: string,
  selectedStatusFilter: string,
  sortColumn: string = 'sessionDateTime', // Default sort column
  sortDirection: 'asc' | 'desc' = 'desc' // Default sort direction
): UsePatientSessionsReturn => {
  const { t } = useTranslation(); // Get the t function
  const { user } = useAuth(); // Get user from context
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState<string | null>(null); // State holds translated error
  const [lastVisible, setLastVisible] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [refreshCounter, setRefreshCounter] = useState(0); // State to trigger refresh

  // Function to set translated error messages
  const setTranslatedError = (key: string) => {
    setError(t(key));
  };

  // Core function to fetch sessions with filters and pagination
  const fetchSessions = useCallback(
    async (cursor?: QueryDocumentSnapshot<DocumentData>) => {
      // Guard clauses
      if (!user || !patientId) {
        console.log(
          'usePatientSessions: User or patientId missing, skipping fetch.'
        );
        setIsLoading(false); // Ensure loading stops
        setIsFetchingMore(false);
        return;
      }

      console.log(
        `usePatientSessions: Fetching sessions for patient ${patientId}, cursor: ${!!cursor}`
      );

      // Set loading state based on whether it's an initial load or loading more
      cursor ? setIsFetchingMore(true) : setIsLoading(true);
      setError(null); // Clear previous errors

      try {
        // Base query for sessions belonging to the therapist and patient
        let baseQuery = query(
          collection(db, 'sessions'),
          where('therapistId', '==', user.uid),
          where('patientId', '==', patientId)
        );

        // Apply date filter
        const dateRange = getDateRange(selectedDateFilter);
        if (dateRange) {
          console.log(
            `Applying date range: ${dateRange.start} - ${dateRange.end}`
          );
          baseQuery = query(
            baseQuery,
            where('sessionDateTime', '>=', Timestamp.fromDate(dateRange.start)),
            where('sessionDateTime', '<=', Timestamp.fromDate(dateRange.end))
          );
        }

        // Apply status filter (skip if 'all')
        if (selectedStatusFilter !== 'all') {
          console.log(`Applying status filter: ${selectedStatusFilter}`);
          baseQuery = query(
            baseQuery,
            where('status', '==', selectedStatusFilter)
          );
        }

        // Add sorting - ensure the sort field is indexed in Firestore, especially if combined with range filters
        // Note: Firestore requires the first orderBy field to match the field used in inequality filters (like date range)
        // If sorting by a different field, adjust query or indexing strategy.
        // Defaulting to sessionDateTime if no other field is used in range filters.
        const effectiveSortColumn =
          dateRange && sortColumn !== 'sessionDateTime'
            ? 'sessionDateTime'
            : sortColumn;
        if (dateRange && sortColumn !== 'sessionDateTime') {
          console.warn(
            `Date range filter applied. Forcing sort by sessionDateTime instead of ${sortColumn} due to Firestore limitations.`
          );
        }
        console.log(`Applying sort: ${effectiveSortColumn} ${sortDirection}`);
        let finalQuery = query(
          baseQuery,
          orderBy(effectiveSortColumn, sortDirection), // Use effective sort column
          limit(SESSIONS_PER_PAGE)
        );

        // Add cursor for pagination if provided
        if (cursor) {
          finalQuery = query(finalQuery, startAfter(cursor));
        }

        // Execute the query
        const querySnapshot = await getDocs(finalQuery);
        const fetchedSessions = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<Session, 'id'>), // Type assertion
        }));
        console.log(`Fetched ${fetchedSessions.length} sessions.`);

        // Update state: append if loading more, replace if initial load
        setSessions((prev) =>
          cursor ? [...prev, ...fetchedSessions] : fetchedSessions
        );
        // Update cursor for next page
        setLastVisible(
          querySnapshot.docs[querySnapshot.docs.length - 1] || null
        );
        // Determine if more pages exist
        setHasMore(querySnapshot.docs.length === SESSIONS_PER_PAGE);
      } catch (err) {
        // Handle errors during fetch
        console.error('usePatientSessions: Error fetching sessions:', err);
        setTranslatedError('hooks.usePatientSessions.errors.loadFailed'); // Use key
      } finally {
        // Reset loading states
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    },
    [
      user,
      patientId,
      selectedDateFilter,
      selectedStatusFilter,
      sortColumn,
      sortDirection,
      t,
    ]
  ); // Add t to dependencies

  // Effect to trigger fetch on initial load, filter changes, or refresh
  useEffect(() => {
    if (user && patientId) {
      console.log(
        'usePatientSessions: Filters or refreshCounter changed, resetting and fetching first page.'
      );
      setSessions([]); // Clear existing sessions
      setLastVisible(null); // Reset pagination cursor
      setHasMore(true); // Assume there's more data initially
      setError(null); // Clear previous errors
      fetchSessions(); // Fetch the first page
    }
    // Intentionally excluding fetchSessions from dependency array here if it causes infinite loops.
    // Relying on the change of its own dependencies (user, patientId, filters, sort, refreshCounter) to trigger the fetch.
  }, [
    user,
    patientId,
    selectedDateFilter,
    selectedStatusFilter,
    sortColumn,
    sortDirection,
    refreshCounter,
  ]); // Dependencies that trigger a reset/refetch

  // Function to load the next page of sessions
  const loadMore = useCallback(async () => {
    // Prevent loading more if already loading, no more data, or no cursor
    if (!isLoading && !isFetchingMore && hasMore && lastVisible) {
      console.log('usePatientSessions: Loading more sessions...');
      await fetchSessions(lastVisible);
    } else {
      console.log('usePatientSessions: Load more condition not met.', {
        isLoading,
        isFetchingMore,
        hasMore,
        lastVisible: !!lastVisible,
      });
    }
  }, [isLoading, isFetchingMore, hasMore, lastVisible, fetchSessions]);

  // Function to manually trigger a refresh (refetches the first page)
  const refresh = useCallback(() => {
    console.log('usePatientSessions: Refresh triggered.');
    setRefreshCounter((prev) => prev + 1);
  }, []);

  // Return the hook's state and functions
  return {
    sessions,
    isLoading,
    isFetchingMore,
    error, // Translated error string
    hasMore,
    loadMore,
    refresh,
  };
};
